import { forwardRef, useImperativeHandle } from 'react';
import { TranslationService } from '../../../services/translation.service';
import { CompensationType } from '../../../models/compensation-type.enum';
import ValidationInfo from '../../../validation/validation-info';
import type IValidatable from '../validatable.interface';
import type { IServerValidationError } from '../validatable.interface';
import ValidationEngine from '../../../validation/validation-engine';

export interface ICompensationTypeSelectorState {
  value: CompensationType;
  validationInfo: ValidationInfo;
}

interface ICompensationTypeSelectorProps {
  availableCompensationTypes: CompensationType[];
  state: ICompensationTypeSelectorState;
  translationBase?: string;
  additionalInfo?: { compensationType: CompensationType; additionalInfo: string }[];
  onChange?: (compensationType: CompensationType) => void;
}

function getTranslationKey(compensationType: CompensationType, translationPrefix: string): string {
  switch (compensationType) {
    case CompensationType.ToGoVoucherByEmail:
      return `${translationPrefix}.togovoucerbyemailcompensationtype`;
    case CompensationType.BankAccount:
      return `${translationPrefix}.bankaccountcompensationtype`;
    default:
      throw new Error(`Unexpected compensation type: ${compensationType}`);
  }
}

const serverValidationName = 'compensationType';

export default forwardRef<IValidatable, ICompensationTypeSelectorProps>(function CompensationTypeSelector(
  { availableCompensationTypes, state, translationBase, additionalInfo, onChange }: ICompensationTypeSelectorProps,
  ref,
) {
  // === VARIABLES ===
  const translationPrefix = translationBase ?? 'customerserviceforms.compensationtype';

  // === HELPERS ===
  function getAdditionalInfo(compensationType: CompensationType): string | undefined {
    return additionalInfo?.find((i) => i.compensationType === compensationType)?.additionalInfo;
  }

  function getAriaDescribedBy(compensationType: CompensationType): string | undefined {
    const validationid = state.validationInfo.isInvalid ? 'compensation-type-validation-error' : undefined;
    const additionalInfoId = getAdditionalInfo(compensationType) ? `additional-info-${compensationType.toLocaleLowerCase()}` : undefined;
    return `${validationid ?? ''} ${additionalInfoId ?? ''}`.trim() || undefined;
  }

  // === INTERFACE IMPLEMENTATION ===
  useImperativeHandle(ref, () => ({
    checkValidity(): ValidationInfo {
      const updatedValidationInfo = new ValidationInfo(`${TranslationService.get(`${translationPrefix}.heading`)} - `);
      ValidationEngine.for(updatedValidationInfo).required(state.value).validate();
      return updatedValidationInfo;
    },
    handleServerValidationErrors(errors: IServerValidationError[]): ValidationInfo {
      return new ValidationInfo(
        `${TranslationService.get(`${translationPrefix}.heading`)} - `,
        errors
          .filter((e) => e.name === serverValidationName)
          .map((e) => e.errorMessages)
          .reduce((a, b) => a.concat(b), []),
      );
    },
  }));

  return (
    <div className="compensation-type">
      <fieldset>
        <legend className="h4 mb-3">{TranslationService.get(`${translationPrefix}.heading`)}</legend>
        <div className="d-flex flex-column gap-3">
          {availableCompensationTypes.map((compensationType) => (
            <div key={compensationType} className={`compensation-type-option ${state.validationInfo.isInvalid ? 'is-invalid' : ''}`}>
              <div className="form-check">
                {/* eslint-disable-next-line jsx-a11y/role-supports-aria-props */}
                <input
                  type="radio"
                  className={`form-check-input ${state.validationInfo.isInvalid ? 'is-invalid' : ''}`}
                  name="compensation-type"
                  id={`compensation-type-${compensationType.toLocaleLowerCase()}`}
                  value={compensationType}
                  checked={state.value === compensationType}
                  onChange={() => onChange?.(compensationType)}
                  aria-invalid={state.validationInfo.isInvalid}
                  aria-describedby={getAriaDescribedBy(compensationType)}
                />
                <label className="form-check-label" htmlFor={`compensation-type-${compensationType.toLocaleLowerCase()}`}>
                  {TranslationService.get(getTranslationKey(compensationType, translationPrefix))}
                </label>
              </div>
            </div>
          ))}
          {state.validationInfo.isInvalid && (
            <div id="compensation-type-validation-error" className="invalid-feedback">
              {state.validationInfo.errors.map((error) => (
                <div key={error}>{error}</div>
              ))}
            </div>
          )}
          {additionalInfo && additionalInfo.length > 0 && (
            <div className="last-child-margin-bottom-none">
              {additionalInfo.map((info) => (
                <p key={info.compensationType} id={`additional-info-${info.compensationType.toLocaleLowerCase()}`}>
                  * {info.additionalInfo}
                </p>
              ))}
            </div>
          )}
        </div>
      </fieldset>
    </div>
  );
});
